// La voz de Alpha: no dice nada, y por eso se entiende.
//
// EL ENCARGO FUE ÉSTE: *«que haga como ruiditos, si son "incomprensibles" ya vale (se puede
// hacer el sonido propio de la música para simular que está hablando)»*. La segunda mitad de la
// frase es la que manda, y es una buena idea: si los ruiditos salen de la misma tonalidad que
// la música del sitio, **nunca desafinan**. Un balbuceo con notas de su casa se oye como parte
// del lugar; el mismo balbuceo con notas cualesquiera se oye como un aviso del navegador.
//
// ASÍ QUE HABLA CON LAS NOTAS DE LA EXPLANADA. Literalmente las mismas: la pentatónica de
// destellos de su tema, `THEMES.explanada.spark`. No hay aquí ninguna escala nueva. El día que
// se cambie la tonalidad del sitio, Alpha cambia de voz con él, porque sólo hay un sitio donde
// está escrita.
//
// Y LA MISMA FRASE PARA EL MISMO TEXTO. No es una floritura: es lo que hace que se pueda
// reproducir. Una voz al azar suena distinta cada vez que se pulsa, y entonces se pulsa por
// oírla; y cuando alguien pregunte «qué le contestó la isla», la respuesta no se puede volver
// a oír. Es la misma decisión que ya está tomada en el backend para las frases de respaldo
// (`sugerencias/npc.py`), y por la misma razón.
//
// Lógica pura, sin WebAudio: aquí están las notas y los tiempos, y quien las toca es
// `lib/sound.js`. Se prueba con `scripts/prueba_voz.mjs`.

// Y SE LEEN DE SU PROPIO FICHERO Y NO DE LA TABLA DE TEMAS, desde el 2 de octubre de 2026.
// Esto importaba `ambient-temas.js` entera —5,6 kB de la música del mundo 3D— para sacarle un
// array de cinco números, y como `lib/sound.js` importa esto, esos 5,6 kB viajaban en la
// PRIMERA CARGA: se los descargaba alguien que sólo quería ver la portada. La escala no se ha
// copiado —eso rompería lo de arriba—: se ha mudado, y los dos la leen del mismo sitio.
import { DESTELLOS_DE_LA_PLAZA } from "./pentatonicaDeLaPlaza.mjs";

/** Las notas con las que habla: las de su sitio, no unas suyas. */
export const NOTAS = DESTELLOS_DE_LA_PLAZA;

/** Lo más larga que puede ser una frase. Pasado esto deja de ser un balbuceo y es una melodía,
 *  y una melodía encima de la música de fondo son dos músicas. */
export const TOPE = 1.2;

const SILABAS = [3, 6];        // de tres a seis: menos no es hablar, más es un discurso

/** Un número estable a partir de un texto. FNV-1a, escrito entero porque tiene que dar lo
 *  mismo hoy y dentro de un año: `Math.random` no vale y en JavaScript no hay `hash` de
 *  cadenas que se pueda prometer. */
function semillaDe(texto) {
  let h = 2166136261;
  const s = String(texto == null ? "" : texto);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Lo que dice Alpha, como lista de sílabas. Cada una:
 *
 *   `{ hz, hasta, cuando, dura, fuerza }`
 *
 * `hasta` es a dónde desliza el tono dentro de la sílaba —eso es lo que la hace sonar a voz y
 * no a pitido— y `cuando` es su hueco desde el principio de la frase.
 *
 * DOS REGLAS QUE NO SON DE ADORNO. Una: **nunca dos sílabas seguidas en la misma nota**, que
 * es lo que convierte un balbuceo en una alarma de horno. Y dos: la última sílaba es más larga
 * y sube, que es como acaba una frase de alguien que espera respuesta.
 */
export function frase(semilla) {
  let s = semillaDe(semilla);
  const tira = (n) => {
    s = (Math.imul(s, 1103515245) + 12345) >>> 0;
    return (s >>> 16) % n;
  };

  const cuantas = SILABAS[0] + tira(SILABAS[1] - SILABAS[0] + 1);
  const silabas = [];
  let nota = tira(NOTAS.length);
  let cuando = 0;

  for (let i = 0; i < cuantas; i++) {
    if (i > 0) {
      // Un paso corto dentro de la pentatónica. Si el borde de la escala lo devolvería a la
      // nota de antes, se rebota hacia el otro lado: el tope de la escala no puede hacer que
      // se repita, porque repetir es justo lo que no puede pasar.
      const salto = [-2, -1, 1, 2][tira(4)];
      let siguiente = nota + salto;
      if (siguiente < 0 || siguiente >= NOTAS.length) siguiente = nota - salto;
      nota = siguiente === nota ? (nota + 1) % NOTAS.length : siguiente;
    }
    const ultima = i === cuantas - 1;
    const dura = ultima ? 0.16 + tira(5) / 100 : 0.055 + tira(4) / 100;
    // El deslizamiento: arriba al final —una frase que espera respuesta sube— y pequeño y
    // hacia donde caiga en las de en medio.
    const desliza = ultima ? 1.14 : 1 + (tira(9) - 4) / 100;
    silabas.push({
      hz: NOTAS[nota],
      hasta: NOTAS[nota] * desliza,
      cuando,
      dura,
      // SE OÍA COMO UN TIC DE INTERFAZ Y NO COMO ALGUIEN HABLANDO: *«el pez debe hablar y no
      // se nota»*. Estaba en 0,055, que es el volumen de un `tick` de menú, y encima compite
      // con la música de fondo. Subido a la banda en la que están los sonidos con los que la
      // casa dice algo —el `chime` va a 0,09 y el arpegio a 0,11—, que es donde tiene que
      // estar una voz. Sigue sin tapar la música: eso lo vigila la prueba.
      fuerza: (ultima ? 0.125 : 0.088) + tira(3) / 200,
    });
    cuando += dura + 0.03 + tira(5) / 100;
  }
  return silabas;
}

/** Lo que dura una frase entera, en segundos. */
export function duracion(silabas) {
  if (!silabas.length) return 0;
  const ultima = silabas[silabas.length - 1];
  return ultima.cuando + ultima.dura;
}
